import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { suporte } from '../api/client';
import { useAuth } from './auth-context';
import { Carregando, Vazio } from '../components/ui';

/**
 * Porta de entrada do app. Enquanto a sessão é reidratada não decide nada —
 * mandar para o login antes de saber se há sessão faria a página piscar a cada
 * F5 de quem está logado.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { usuario, carregando } = useAuth();
  const location = useLocation();

  if (carregando) return <Carregando texto="Verificando sessão…" />;

  if (!usuario) {
    // `state.de` devolve a pessoa para onde ela queria ir depois do login.
    return <Navigate to="/entrar" replace state={{ de: location.pathname }} />;
  }

  /**
   * Superadmin em visão global não entra nas telas do cliente.
   *
   * Espelho do `RequireSuperadmin`: lá o admin de organização é mandado embora
   * do Master; aqui o Superadmin é mandado embora do painel do cliente. Sem
   * isto, `/usuarios` listava usuários de TODAS as organizações numa tela feita
   * para uma só, e `/eventos` ficava preso em "Carregando…" porque a API
   * recusava (modelo 'strict' exige `runAsPlatform`) sem a tela dizer nada.
   *
   * Com sessão de suporte aberta ele passa: é exatamente o caminho legítimo
   * para operar o ambiente de um cliente, e fica registrado.
   *
   * `/conta` fica de fora porque é a PRÓPRIA conta, não a do cliente — mesma
   * razão do `@AnyScope()` no `/auth/change-password`. O Master ainda não tem
   * tela de conta própria; enquanto não tiver, bloquear aqui deixaria o
   * Superadmin sem como trocar a própria senha pela interface.
   */
  const contaPropria = location.pathname === '/conta';

  if (usuario.scope === 'SUPERADMIN' && !suporte.atual() && !contaPropria) {
    return <Navigate to="/master" replace />;
  }

  return <>{children}</>;
}

/**
 * Porta do painel Master.
 *
 * Redireciona em vez de mostrar "sem acesso": para quem é de uma organização, o
 * Master não é uma área bloqueada — é uma área que não existe. Quem chega aqui
 * por link ou favorito vai para o próprio painel, sem beco sem saída.
 *
 * Isto é conveniência de navegação, não segurança: o `@SuperadminOnly()` do
 * backend é que barra de verdade, e continua barrando se alguém chamar a API na
 * mão.
 */
export function RequireSuperadmin({ children }: { children: ReactNode }) {
  const { usuario, carregando } = useAuth();

  if (carregando) return <Carregando texto="Verificando sessão…" />;
  if (!usuario) return <Navigate to="/entrar" replace state={{ de: '/master' }} />;
  if (usuario.scope !== 'SUPERADMIN') return <Navigate to="/dashboard" replace />;

  /**
   * Sessão de suporte aberta trava a porta do Master.
   *
   * O escopo continua SUPERADMIN, então sem esta linha dava para voltar ao
   * painel da plataforma ainda "dentro" do cliente — e como todas as chamadas
   * saem com o token de suporte, o painel mostrava o recorte daquele cliente
   * como se fosse a plataforma. A saída é encerrar o acesso, e o botão para
   * isso está na faixa roxa do `/dashboard`, que é para onde mandamos de volta.
   */
  if (suporte.atual()) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
}

/**
 * Etapa construída, ainda não liberada nesta instalação.
 *
 * Mostra "Em breve" em vez de redirecionar: quem chega por favorito ou endereço
 * digitado merece saber que a tela existe e por que não abre — um desvio mudo
 * para o dashboard pareceria bug.
 *
 * Como o `RequirePermission`, isto é navegação e não segurança: quem chamar a
 * API direto continua barrado pelo `RecursosGuard` do servidor.
 */
export function RequireRecurso({
  recurso,
  children,
}: {
  recurso: 'jornadas';
  children: ReactNode;
}) {
  const { liberado } = useAuth();

  if (!liberado(recurso)) {
    return (
      <Vazio
        icone="ti-clock-hour-3"
        titulo="Em breve"
        descricao="Esta etapa já existe no sistema, mas ainda não foi liberada nesta instalação. Fale com a MatériaPrimAi quando quiser ativá-la."
      />
    );
  }

  return <>{children}</>;
}

/**
 * Bloqueio por permissão no cliente.
 *
 * É conveniência de navegação, NÃO segurança: quem chamar a API direto continua
 * sendo barrado pelos guards do servidor. Aqui só evita mostrar uma tela que
 * responderia 403 em toda consulta.
 */
export function RequirePermission({
  permissoes,
  superadmin,
  children,
}: {
  permissoes?: string[];
  superadmin?: boolean;
  children: ReactNode;
}) {
  const { pode, ehSuperadmin } = useAuth();

  const liberado = superadmin ? ehSuperadmin : !permissoes?.length || pode(...permissoes);

  if (!liberado) {
    return (
      <Vazio
        icone="ti-lock"
        titulo="Sem acesso a esta área"
        descricao={
          superadmin
            ? 'Esta área é da administração da plataforma MatériaPrimAi.'
            : 'Seu papel não inclui a permissão necessária. Fale com o administrador da sua organização.'
        }
      />
    );
  }

  return <>{children}</>;
}
